import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { AlertTriangle, Zap, Calendar, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SVGMapViewer } from "@/components/SVGMapViewer";
import { StormTypeLegend } from "@/components/StormTypeBadge";
import { LightningActivityIndex } from "@/components/LightningActivityIndex";
import { ThreatBreakdown } from "@/components/ThreatIndicators";
import { SnowfallTotals, SeverePhenomena } from "@/components/SnowfallInfo";
import { formatValidityPeriod } from "@/lib/utils";

export default function HomePage() {
  const [outlook, setOutlook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOutlook();

    const channel = supabase
      .channel("public-home-outlook")
      .on("postgres_changes", { event: "*", schema: "public", table: "outlooks" }, () => {
        fetchOutlook();
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  const fetchOutlook = async () => {
    try {
      setLoading(true);
      // Try to get the active outlook first
      let { data: activeData } = await supabase
        .from("outlooks")
        .select("*")
        .eq("is_active", true)
        .eq("is_published", true)
        .maybeSingle();

      if (activeData) {
        setOutlook(activeData);
        return;
      }

      // Fall back to most recently published outlook
      const { data, error: fetchError } = await supabase
        .from("outlooks")
        .select("*")
        .eq("is_published", true)
        .order("outlook_date", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fetchError) throw fetchError;
      setOutlook(data || null);
    } catch (err) {
      setError("Failed to load outlook");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const outlookSlug = outlook
    ? outlook.outlook_date || outlook.id
    : null;

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#FBBF24] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#9CA3AF] text-sm uppercase tracking-wider">Loading outlook...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16">
        <div className="text-center py-20 border border-white/10 bg-[#111827]">
          <AlertTriangle className="w-16 h-16 text-[#6B7280] mx-auto mb-4" />
          <h2 className="font-heading text-2xl text-[#F9FAFB] uppercase tracking-wide mb-2">
            Error Loading Outlook
          </h2>
          <p className="text-[#9CA3AF] mb-6">{error}</p>
          <Button
            onClick={fetchOutlook}
            className="bg-[#FBBF24] text-[#0B1220] hover:bg-[#F59E0B] font-bold uppercase tracking-wider rounded-none px-6"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!outlook) {
    return (
      <div className="animate-fade-in" data-testid="home-page">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16">
          <div className="text-center py-20 border border-white/10 bg-[#111827]">
            <Zap className="w-16 h-16 text-[#6B7280] mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-[#F9FAFB] uppercase tracking-wide mb-2">
              No Outlooks Yet
            </h2>
            <p className="text-[#9CA3AF] mb-6">
              Check back later for outlook updates.
            </p>
            <Link to="/archive">
              <Button className="bg-[#FBBF24] text-[#0B1220] hover:bg-[#F59E0B] font-bold uppercase tracking-wider rounded-none">
                View Archive
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="home-page">
      {/* Status bar */}
      <div className="border-b border-white/5 bg-[#111827]/60 py-2">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
            {outlook.is_active ? (
              <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[#22C55E] flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
                Active Outlook
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-widest text-[#6B7280] flex-shrink-0">
                Most Recent Outlook
              </span>
            )}
            <span className="text-[#6B7280] text-[10px] flex-shrink-0">|</span>
            <span className="font-mono text-[10px] text-[#9CA3AF] flex items-center gap-1 truncate">
              <Calendar className="w-3 h-3 flex-shrink-0" />
              {outlook.outlook_date
                ? new Date(outlook.outlook_date + "T00:00:00").toLocaleDateString("en-GB", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : new Date(outlook.created_at).toLocaleDateString("en-GB", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
            </span>
            {formatValidityPeriod(outlook.valid_from, outlook.valid_to) && (
              <>
                <span className="text-[#6B7280] text-[10px] hidden sm:inline flex-shrink-0">|</span>
                <span className="font-mono text-[10px] text-[#9CA3AF] hidden sm:flex items-center gap-1 flex-shrink-0">
                  <Clock className="w-3 h-3" />
                  {formatValidityPeriod(outlook.valid_from, outlook.valid_to)}
                </span>
              </>
            )}
          </div>
          <Link to="/archive">
            <Button
              variant="ghost"
              size="sm"
              className="text-[#6B7280] hover:text-[#F9FAFB] text-xs uppercase tracking-wider h-7 px-3"
            >
              Archive
            </Button>
          </Link>
        </div>
      </div>

      {/* Main layout: map (left) + info panel (right) */}
      <section className="container mx-auto px-4 md:px-8 max-w-7xl py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left – SVG Map (dominant visual) */}
          <div className="lg:col-span-8 flex flex-col gap-3">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-[#F9FAFB] uppercase tracking-tight leading-tight">
              {outlook.headline || outlook.title}
            </h1>
            <SVGMapViewer
              svgContent={outlook.svg_content}
              className="w-full"
            />
          </div>

          {/* Right – Info Panel */}
          <div className="lg:col-span-4 space-y-4">
            {outlook.type !== "snowfall" ? (
              <>
                {/* Thunderstorm Types */}
                {outlook.storm_types && outlook.storm_types.length > 0 && (
                  <div className="bg-[#111827] border border-white/10 p-4 space-y-3">
                    <h3 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                      Thunderstorm Types
                    </h3>
                    <StormTypeLegend types={outlook.storm_types} />
                  </div>
                )}

                {/* Lightning Activity Index */}
                {outlook.lightning_activity != null && (
                  <div className="bg-[#111827] border border-white/10 p-4">
                    <LightningActivityIndex value={outlook.lightning_activity ?? 0} />
                  </div>
                )}

                {/* Threat Breakdown */}
                <div className="bg-[#111827] border border-white/10 p-4">
                  <ThreatBreakdown outlook={outlook} />
                </div>
              </>
            ) : (
              <>
                {/* Severe Phenomena */}
                {outlook.severe_phenomena && outlook.severe_phenomena.length > 0 && (
                  <div className="bg-[#111827] border border-white/10 p-4">
                    <SeverePhenomena phenomena={outlook.severe_phenomena} />
                  </div>
                )}

                {/* Snowfall Totals */}
                {outlook.snowfall_totals && outlook.snowfall_totals.length > 0 && (
                  <div className="bg-[#111827] border border-white/10 p-4">
                    <SnowfallTotals totals={outlook.snowfall_totals} />
                  </div>
                )}
              </>
            )}

            {/* Description */}
            {outlook.description && (
              <div className="bg-[#111827] border border-white/10 p-4 space-y-2">
                <h3 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                  Overview
                </h3>
                <p className="text-[#9CA3AF] text-sm leading-relaxed">
                  {outlook.description}
                </p>
              </div>
            )}

            {/* View Full Outlook button */}
            {outlookSlug && (
              <Link to={`/outlook/${outlookSlug}`} className="block">
                <Button className="w-full bg-[#FBBF24] text-[#0B1220] hover:bg-[#F59E0B] font-bold uppercase tracking-wider rounded-none">
                  View Full Outlook
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
